import { useCallback, useRef } from 'react';

import { newClientRequestId } from './api';

/**
 * One idempotency key per submission (API-CONTRACTS §2.6). A question, an analysis, a registration or a sync is real
 * work on the server and spends the organization's quota; a double click, a retry after a lost answer, or the
 * automatic retry of a request that never reached Desk must not do it twice.
 *
 * `start(payload)` begins a submission and returns the key to send — or null while the previous submission is still
 * on its way, so that a second click sends nothing (checked synchronously: the button's disabled state only follows
 * on the next render). The key stays the same for the same payload until a submission succeeds, so trying again after
 * a failure is the same submission, and the server answers with what the first attempt made if it got that far.
 * `settle(succeeded)` ends the submission; after a success, the next one — even with the same words — is new work.
 */
export function useSubmissionKey() {
  const last = useRef<{ fingerprint: string; key: string } | null>(null);
  const inFlight = useRef(false);

  const start = useCallback((payload: unknown): string | null => {
    if (inFlight.current) {
      return null;
    }
    inFlight.current = true;
    const fingerprint = JSON.stringify(payload);
    if (last.current?.fingerprint !== fingerprint) {
      last.current = { fingerprint, key: newClientRequestId() };
    }
    return last.current.key;
  }, []);

  const settle = useCallback((succeeded: boolean) => {
    inFlight.current = false;
    if (succeeded) {
      last.current = null;
    }
  }, []);

  /** Mutation callbacks that settle the submission. */
  const callbacks = useCallback(
    <T>(onSuccess?: (result: T) => void) => ({
      onSuccess: (result: T) => {
        settle(true);
        onSuccess?.(result);
      },
      onError: () => settle(false),
    }),
    [settle],
  );

  return { start, settle, callbacks };
}
